// src/components/ControlledSelect/index.tsx
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { Box, Menu, MenuButton, MenuItem, MenuList, Button, Image, Text, SystemStyleObject } from '@chakra-ui/react';
import { ChakraFeatherIcon } from '../ChakraFeatherIcon';
import { useTranslation } from 'react-i18next';

type ControlProps<T extends FieldValues> = Pick<UseControllerProps<T>, 'control' | 'name' | 'rules' | 'disabled'>;

type ControlledSelectProps<T extends FieldValues> = ControlProps<T> & {
  width?: string | number | Record<string, any>;
  variant?: 'flat' | 'outline';
  options: Array<{
    label?: string;
    value: string;
    imageUrl: string;
  }>;
  onSelect?: (value: string) => void;
  label?: string;
  error?: string;
  placeholder?: boolean;
  disabled?: boolean;
};

export function ControlledSelect<T extends FieldValues>({
  variant = 'flat',
  name,
  control,
  options,
  onSelect,
  label,
  error,
  width,
  placeholder,
  disabled,
}: ControlledSelectProps<T>) {
  const { t } = useTranslation();

  const styleByVariant: Record<'flat' | 'outline', SystemStyleObject> = {
    flat: {
      fontSize: 'var(--input-font-size)',
      py: 1,
      borderBottomWidth: error ? '2px' : '1px',
      borderColor: error ? 'red.500' : 'black',
      borderBottomStyle: 'solid',
      _expanded: {
        borderBottom: '2px solid',
        borderColor: 'brand.500',
      },
    },
    outline: {
      fontSize: 'var(--input-font-size)',
      py: 2,
      px: 3,
      border: '1px solid',
      borderColor: error ? 'red.500' : 'gray.300',
      borderRadius: 8,
      _expanded: {
        outline: '2px solid!important',
        outlineColor: 'brand.300!important',
      },
    },
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const selectedOption = options?.find(option => option.value === value);

        return (
          <Box w={width}>
            {label ? <Text fontWeight='medium'>{label}</Text> : null}

            <Menu>
              <MenuButton
                isDisabled={disabled}
                as={Button}
                w={width}
                rightIcon={<ChakraFeatherIcon name='chevron-down' w='20px' />}
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                gap={2}
                textAlign='left'
                __css={{
                  ...styleByVariant[variant],
                  ...(disabled && {
                    color: 'gray.500',
                    cursor: 'not-allowed',
                    _hover: { bg: 'gray.100' },
                  }),
                }}
              >
                {selectedOption ? (
                  <Box display='flex' alignItems='center'>
                    <Image src={selectedOption.imageUrl} alt={selectedOption.label} boxSize='20px' mr='10px' />
                    <>{selectedOption?.label ?? ''}</>
                  </Box>
                ) : (
                  t('components.controlled_select.controlled_select_placeholder')
                )}
              </MenuButton>

              <MenuList>
                {placeholder && (
                  <MenuItem
                    key=''
                    isDisabled={disabled}
                    onClick={() => {
                      if (!disabled) {
                        onChange('');
                        onSelect?.('');
                      }
                    }}
                  >
                    <>{t('components.controlled_select.controlled_select_placeholder')}</>
                  </MenuItem>
                )}

                {options.map(option => (
                  <MenuItem
                    isDisabled={disabled}
                    key={option.value}
                    value={option.value}
                    onClick={() => {
                      if (!disabled) {
                        onChange(option.value);
                        onSelect?.(option.value);
                      }
                    }}
                  >
                    <Box display='flex' gap={2} alignItems='center'>
                      <Image src={option.imageUrl} alt={option.label} boxSize='20px' />
                      <>{option?.label ?? ''}</>
                    </Box>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            {error ? (
              <Text fontSize='14px' color='red.500' mt={2}>
                {error}
              </Text>
            ) : null}
          </Box>
        );
      }}
    />
  );
}
